/** Экран 1 — акция: условия, участники, победители / результаты. */
import type { Campaign, OfferView, PublicTicket } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import {
  Badge, BrandMark, Button, Card, Countdown, Empty, Hash, Icon, KeyRow, Progress, Segmented, Skeleton, VerifiedMark, fmtDateTime, fmtDay, fmtShortDay, num, percent,
} from '@allswipes/ui';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { linkFor, rememberLink } from '../link';
import { Fairness } from '../components/Fairness';
import { RatingLine, ReviewList } from '../components/Rating';
import { Shell } from '../components/Shell';
import { SuspendedNote } from '../components/Suspended';
import { CampaignVideo } from '../components/CampaignVideo';
import { useResource } from '../hooks';
import { useSession } from '../session';
import { useToast } from '../toast';

import { useT } from '@allswipes/i18n';

type Tab = 'cond' | 'part' | 'win' | 'res';

export function CampaignScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { signedIn } = useSession();
  const toast = useToast();

  // Ключ канала приезжает в адресе с баннера и запоминается на сессию: до
  // гашения кода человек успевает уйти на другой экран, а надбавка должна
  // дойти — иначе обещание с баннера не сбудется.
  const key = params.get('k');
  rememberLink(id, key);
  const link = key ?? linkFor(id);

  const view = useResource(() => api.campaign(id, link), [id, link]);
  // Условие канала грузится отдельно: оно живёт своей жизнью — у него свой
  // срок и свои места, и карточка акции о нём ничего не знает.
  const channel = useResource(() => (link ? api.link(link) : Promise.resolve(null)), [link]);
  const [tab, setTab] = useState<Tab>('cond');
  const [rulesOpen, setRulesOpen] = useState(false);
  // Подпись к офферу держит экран, а не кнопка: показать её надо под рядом
  // кнопок. Внутри ряда она вставала ещё одной колонкой и разваливалась на
  // отдельные слова между кнопками.
  const [offerNote, setOfferNote] = useState('');

  if (view.loading) {
    return (
      <Shell>
        <Skeleton count={3} height={120} />
      </Shell>
    );
  }
  if (view.error || !view.data) {
    return (
      <Shell title={t('Акция')}>
        <Empty icon="search" title={t('Акция не найдена')}>
          {view.error ?? t('Возможно, ссылка устарела.')}
        </Empty>
      </Shell>
    );
  }

  const { campaign: c, brand, odds, odds_total: total, fair, winners, results, participants, reminded } =
    view.data;
  const hasDraw = Boolean(c.draw_at);
  // Бренд снят платформой с витрины: страница остаётся (на ней правила,
  // участники и доказательства), но звать участвовать нельзя — сервер всё
  // равно откажется принять код.
  const suspended = Boolean(brand?.blocked_at);
  const myNumbers = new Set((view.data.my_tickets ?? []).map((t) => t.number));

  const tabs: Array<{ value: Tab; label: string }> = [{ value: 'cond', label: t('Условия') }];
  if (c.status !== 'scheduled') {
    if (hasDraw) {
      tabs.push({ value: 'part', label: t('Участники') });
      if (c.status === 'drawn') tabs.push({ value: 'win', label: t('Победители') });
    } else {
      tabs.push({ value: 'res', label: t('Результаты') });
    }
  }

  const toggleReminder = async () => {
    try {
      await api.setReminder(c.id, hasDraw && c.status !== 'scheduled' ? 'draw' : 'start', !reminded);
      toast(reminded ? t('Напоминание снято') : t('Напомним заранее'));
      view.reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : t('Не получилось'));
    }
  };

  const offer = channel.data?.link.campaign_id === c.id ? channel.data : null;
  // Ключ канала едет дальше в адресе: нажав «Отсканировать код», человек не
  // должен потерять условие, за которым пришёл. Сессия его тоже помнит, но
  // адрес переживает и новую вкладку, и пересланную ссылку.
  const withKey = (path: string) => (link ? `${path}${path.includes('?') ? '&' : '?'}k=${link}` : path);

  return (
    <Shell>
      {/* Условие канала — выше всего: человек пришёл с баннера именно за ним,
          и искать обещание среди условий акции ему незачем. */}
      {offer && (
        <Card className={`chan ${offer.active ? '' : 'off'}`} style={{ marginBottom: 14 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="chan-h">
              {offer.active ? t('Условие по твоей ссылке') : t('Условие по ссылке закончилось')}
            </div>
            <div className="chan-o">{offer.link.offer}</div>
            <div className="chan-m">
              {offer.link.bonus_points > 0 && (
                <span>{t('+{n} баллов сверх обычных', { n: num(offer.link.bonus_points) })}</span>
              )}
              {offer.left >= 0 && (
                <span>
                  {t('осталось {left} из {cap} мест', {
                    left: num(offer.left),
                    cap: num(offer.link.cap),
                  })}
                </span>
              )}
              {offer.used && <span>{t('ты уже воспользовался')}</span>}
            </div>
          </div>
          {offer.active && (offer.expires_at || offer.link.ends_at) && (
            <div style={{ textAlign: 'right' }}>
              <Countdown to={offer.expires_at ?? offer.link.ends_at ?? ''} />
              <div className="faint" style={{ fontSize: 11 }}>
                {offer.expires_at ? t('твоё время по этой ссылке') : t('до конца условия')}
              </div>
            </div>
          )}
          {offer.active && offer.link.window_minutes > 0 && !offer.window_started && (
            <div style={{ textAlign: 'right' }}>
              <div className="mono" style={{ fontSize: 17, color: 'var(--head)' }}>
                {t('{n} мин', { n: offer.link.window_minutes })}
              </div>
              <div className="faint" style={{ fontSize: 11 }}>
                {t('отсчёт начнётся после входа')}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Скрытая карточка без ссылки: честно говорим, что условия откроет
          ссылка бренда. Врать «акции нет» нельзя — человек видел её в ленте. */}
      {c.locked && (
        <Card className="chan locked" style={{ marginBottom: 14 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="chan-h">{t('Условия открываются по ссылке')}</div>
            <div className="chan-o">{t('Приз и условия этой акции видят те, кто пришёл по ссылке бренда')}</div>
            <div className="chan-m">
              <span>{t('с баннера, флаера или от блогера')}</span>
              <span>{t('правила акции открыты всем')}</span>
            </div>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 14 }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <BrandMark name={brand?.name ?? c.prefix} logoUrl={brand?.logo_url} size={38} />
          <div style={{ minWidth: 0 }}>
            <Link to={brand ? `/b/${brand.slug}` : '#'} className="h2">
              {brand?.name}
            </Link>
            <div className="faint" style={{ fontSize: 12 }}>
              {c.regions.join(' · ')}
              {c.age18 ? ' · 18+' : ''}
            </div>
            {brand?.rating && brand.rating.count > 0 && (
              <div style={{ marginTop: 4 }}>
                <RatingLine rating={brand.rating} />
              </div>
            )}
          </div>
          <span className="spacer" />
          {c.status === 'active' && (
            <Badge tone="live" dot>
              {t('идёт')}
            </Badge>
          )}
          {c.status === 'scheduled' && (
            <Badge>{t('старт {day}', { day: fmtShortDay(c.start_at) })}</Badge>
          )}
          {c.status === 'ended' && <Badge tone="warn">{t('ждём розыгрыш')}</Badge>}
          {c.status === 'drawn' && <Badge tone="good">{t('итоги')}</Badge>}
        </div>

        <h1 className="h1">{c.prize_title || c.name}</h1>
        {c.tagline && <p className="sub" style={{ marginTop: 6 }}>{c.tagline}</p>}

        {c.status !== 'drawn' && (
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <div className="lab">
              {c.status === 'scheduled' ? t('до старта') : hasDraw ? t('до розыгрыша') : t('до конца акции')}
            </div>
            <div className="cd" style={{ fontSize: 26 }}>
              <Countdown to={c.status === 'scheduled' ? c.start_at : hasDraw && c.draw_at ? c.draw_at : c.end_at} />
            </div>
          </div>
        )}

        <div className="grid c3" style={{ marginTop: 16 }}>
          <Stat label={t('участников')} value={num(c.participants)} />
          {hasDraw ? (
            <>
              <Stat label={t('билетов')} value={num(c.tickets)} />
              <Stat label={t('призов')} value={num(c.winner_count)} />
            </>
          ) : (
            <Stat label={t('попыток')} value={num(c.tickets)} />
          )}
        </div>

        {view.data.chance && (
          <p className="sub" style={{ marginTop: 12 }}>
            {t('Шанс одного билета при текущем числе участников — {chance}%. Чем больше участников, тем ниже шанс: мы показываем его честно, а не прячем.', { chance: view.data.chance })}
          </p>
        )}

        {c.prizes_verified_at && (
          <div className="vrow" style={{ marginTop: 12 }}>
            <VerifiedMark size={16} />
            <span>
              <strong>{t('Призы проверены платформой.')}</strong>{' '}
              {t('Бренд приложил доказательство призового фонда, мы его посмотрели. Это не гарантия выдачи — за неё отвечает организатор, — но обещание больше не висит в воздухе.')}
              {/* Снимок открывается только вошедшему: на фото склада видно адрес,
                  накладную и чужие товары. Публична проверка, а не улика. */}
              {c.prize_proof_url ? (
                <a className="proof" href={c.prize_proof_url} target="_blank" rel="noreferrer">
                  <img src={c.prize_proof_url} alt="" />
                  <span>
                    {c.prize_proof_note || t('Доказательство призового фонда')}
                    <i>{t('открыть снимок')}</i>
                  </span>
                </a>
              ) : (
                !signedIn && (
                  <Link className="proof" to={`/login?next=/c/${c.id}`}>
                    <span className="ph" aria-hidden />
                    <span>
                      {t('Доказательство открывается после входа')}
                      <i>{t('на снимке склада видно адрес и накладную — он не для открытого интернета')}</i>
                    </span>
                  </Link>
                )
              )}
            </span>
          </div>
        )}

        {c.video_url && (
          <div style={{ marginTop: 14 }}>
            <CampaignVideo url={c.video_url} />
          </div>
        )}

        {suspended && (
          <div style={{ marginTop: 14 }}>
            <SuspendedNote reason={brand?.blocked_reason} />
          </div>
        )}

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          {!suspended && c.status === 'active' && c.recipe === 'quiz' && (
            <Link to={withKey(`/c/${c.id}/quiz`)} className="btn pri block">
              {t('Пройти квиз')}
            </Link>
          )}
          {!suspended && c.status === 'active' && c.recipe === 'streak' && (
            <Link to={withKey(`/c/${c.id}/streak`)} className="btn pri block">
              {t('Отметить день')}
            </Link>
          )}
          {!suspended && c.status === 'active' && c.recipe === 'referral' && (
            <Link to={withKey(`/c/${c.id}/invite`)} className="btn pri block">
              {t('Пригласить друга')}
            </Link>
          )}
          {!suspended && c.status === 'active' && c.recipe === 'quest' && (
            <Link to={withKey(`/c/${c.id}/quest`)} className="btn pri block">
              {t('Открыть цепочку')}
            </Link>
          )}
          {!suspended && c.status === 'active' && c.recipe === 'data4value' && (
            <Link to={withKey(`/c/${c.id}/deal`)} className="btn pri block">
              {t('Посмотреть условия обмена')}
            </Link>
          )}
          {!suspended && c.status === 'active' && c.recipe === 'offer' && (
            <OfferAction campaignId={c.id} linkKey={withKey('')} setNote={setOfferNote} />
          )}
          {!suspended && c.status === 'active' && c.recipe === 'pack' && (
            <a href="#pickup" className="btn pri block">
              {t('Где получить')}
            </a>
          )}
          {!suspended && c.status === 'active' && needsCode(c.recipe) && (
            <Link to={withKey('/scan')} className="btn pri block">
              {t('Отсканировать код')}
            </Link>
          )}
          {signedIn && c.status !== 'drawn' && (
            <Button
              variant="out"
              icon={reminded ? 'check' : 'bell'}
              onClick={toggleReminder}
            >
              {reminded ? t('Напомним') : t('Напомнить')}
            </Button>
          )}
          {c.status === 'drawn' && (
            <Link to={`/draw/${c.id}`} className="btn pri block">
              {t('Отчёт о розыгрыше')}
            </Link>
          )}
        </div>

        {offerNote && (
          <p className="sub" style={{ marginTop: 10 }}>
            {offerNote}
          </p>
        )}
      </Card>

      {/*
        Приз на упаковке. Случайности нет — её напечатал завод, — поэтому
        показывать здесь нечего, кроме двух чисел: объявлено и выдано. Это и
        есть вся проверяемость механики, и прятать её под «подробности»
        нельзя: без неё акция остаётся просто объявлением.
      */}
      {(c.config?.pack?.length ?? 0) > 0 && (
        <Card style={{ marginBottom: 14 }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="h2">{t('Призы и сколько их осталось')}</span>
          </div>
          {(c.config?.pack ?? []).map((row) => {
            const issued = row.issued ?? 0;
            const left = Math.max(0, row.count - issued);
            return (
              <div key={row.label} style={{ marginBottom: 12 }}>
                <div className="row" style={{ marginBottom: 4 }}>
                  <span>{row.label}</span>
                  <span className="spacer" />
                  <span className="mono faint" style={{ fontSize: 12 }}>
                    {left > 0 ? t('осталось {n}', { n: num(left) }) : t('разобрали')}
                  </span>
                </div>
                <Progress value={issued} max={row.count} />
                <p className="sub" style={{ marginTop: 4, fontSize: 12 }}>
                  {t('выдано {issued} из {count}', { issued: num(issued), count: num(row.count) })}
                </p>
              </div>
            );
          })}
          <p className="sub" style={{ marginTop: 4 }}>
            {t('Приз напечатан на упаковке производителем. Платформа его не выбирает — она считает выданное, и это число видно всем.')}
          </p>
        </Card>
      )}

      {(c.config?.pickup?.length ?? 0) > 0 && (
        <div id="pickup">
        <Card style={{ marginBottom: 14 }}>
          <div className="h2" style={{ marginBottom: 10 }}>{t('Где забрать')}</div>
          {(c.config?.pickup ?? []).map((point) => (
            <div key={`${point.name}${point.address ?? ''}`} className="spotrow" style={{ marginBottom: 8 }}>
              <div>
                <div>{point.name}</div>
                {point.address && (
                  <div className="sub" style={{ fontSize: 12 }}>{point.address}</div>
                )}
                {point.note && (
                  <div className="faint" style={{ fontSize: 12 }}>{point.note}</div>
                )}
              </div>
              <span className="spacer" />
              {point.hours && (
                <span className="mono faint" style={{ fontSize: 12 }}>{point.hours}</span>
              )}
            </div>
          ))}
          <p className="sub" style={{ marginTop: 6, fontSize: 12 }}>
            {t('Адреса объявлены до старта и входят в правила акции.')}
          </p>
        </Card>
        </div>
      )}

      {view.data.flash && (
        <Card style={{ marginBottom: 14 }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="h2">{t('Места')}</span>
            <span className="spacer" />
            {view.data.flash.sold_out ? (
              <Badge tone="bad">{t('места закончились')}</Badge>
            ) : (
              <Badge tone="good">{t('свободно {n}', { n: num(view.data.flash.left) })}</Badge>
            )}
          </div>
          <Progress value={view.data.flash.taken} max={view.data.flash.spots} />
          <p className="sub" style={{ marginTop: 10 }}>
            {t('Занято {taken} из {spots}. Места раздаются по очереди — по времени подтверждения кода на сервере, без розыгрыша.', {
              taken: num(view.data.flash.taken),
              spots: num(view.data.flash.spots),
            })}
          </p>
          {Boolean(view.data.flash.my_seq) && (
            <div className="spotrow me" style={{ marginTop: 8 }}>
              <span className="pos">№{view.data.flash.my_seq}</span>
              <span className="h">{t('твоё место')}</span>
              <Icon name="check" size={14} />
            </div>
          )}
        </Card>
      )}

      {view.data.collection && (
        <Card style={{ marginBottom: 14 }}>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="h2">{t('Твой набор')}</span>
            <span className="spacer" />
            <span className="mono faint" style={{ fontSize: 12 }}>
              {view.data.collection.owned}/{view.data.collection.total}
            </span>
          </div>
          <div className="cells">
            {view.data.collection.items.map((item, i) => (
              <div key={i} className={`cell ${item.owned ? 'have' : ''}`}>
                <span className="ic">
                  <Icon name={item.owned ? 'check' : 'gift'} size={22} />
                </span>
                <span className="nm">{item.label}</span>
              </div>
            ))}
          </div>
          <p className="sub" style={{ marginTop: 10 }}>
            {view.data.collection.complete
              ? t('Набор собран — приз «{prize}» гарантирован, без розыгрыша.', {
                  prize: view.data.collection.set_prize ?? '',
                })
              : t('Собери все {total} — приз «{prize}» выдаётся за полный набор, без розыгрыша.', {
                  total: view.data.collection.total,
                  prize: view.data.collection.set_prize ?? '',
                })}
          </p>
        </Card>
      )}

      {(view.data.my_tickets?.length ?? 0) > 0 && (
        <Card style={{ marginBottom: 14 }}>
          <div className="lab" style={{ marginBottom: 8 }}>
            {t('Твои билеты')}
          </div>
          <div className="rail">
            {view.data.my_tickets!.map((t) => (
              <div key={t.id} className="ticket" style={{ minWidth: 170 }}>
                <div className="num">{t.number}</div>
                <div className="meta">{fmtShortDay(t.created_at)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tabs.length > 1 && (
        <div style={{ marginBottom: 14 }}>
          <Segmented value={tab} options={tabs} onChange={setTab} />
        </div>
      )}

      {tab === 'cond' && (
        <div className="stack">
          {(view.data.campaign.extensions?.length ?? 0) > 0 && (
            <Card>
              <div className="row" style={{ marginBottom: 8, gap: 9 }}>
                <Icon name="calendar" size={16} />
                <span className="h2">{t('Срок переносился')}</span>
              </div>
              <p className="sub" style={{ marginBottom: 10 }}>
                {t('Условие переноса объявлено в правилах до старта. Каждый перенос записан здесь — с прежней и новой датой.')}
              </p>
              {view.data.campaign.extensions!.map((ext, i) => (
                <div key={i} className="moved">
                  <span className="dates">
                    {fmtShortDay(ext.from)} → {fmtShortDay(ext.to)}
                  </span>
                  <span className="why">{ext.reason}</span>
                </div>
              ))}
            </Card>
          )}

          {view.data.campaign.config.repeat &&
            view.data.campaign.config.repeat.period !== 'free' && (
              <Card>
                <div className="row" style={{ gap: 9 }}>
                  <Icon name="refresh" size={16} />
                  <span className="h2">{t('Сколько раз можно участвовать')}</span>
                </div>
                <p className="sub" style={{ marginTop: 6 }}>
                  {t('{limit}. Ограничение в правилах акции, пункт 1.6 — оно не появляется в момент отказа.', {
                    limit: t(repeatLabel(view.data.campaign.config.repeat.period)),
                  })}
                </p>
              </Card>
            )}

          {(view.data.campaign.config.fields?.length ?? 0) > 0 && (
            <Card>
              <div className="row" style={{ gap: 9 }}>
                <Icon name="shield" size={16} />
                <span className="h2">{t('Бренд запрашивает данные')}</span>
              </div>
              <p className="sub" style={{ marginTop: 6 }}>
                {t('Для участия нужно передать: {fields}. Перед передачей ты увидишь сами значения и сможешь отказаться — код при этом не потратится.', {
                  fields: fieldLabels(view.data.campaign.config.fields!, t),
                })}
              </p>
            </Card>
          )}
          <Card>
            <div className="h2" style={{ marginBottom: 10 }}>
              {t('Как участвовать')}
            </div>
            <ol className="steps">
              {howTo(c, hasDraw, t).map((step, i) => (
                <li className="st" key={step.title}>
                  <span className="n">{i + 1}</span>
                  <div>
                    <div className="h2">{step.title}</div>
                    <div className="sub">{step.body}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Card>

          {odds && odds.length > 0 && (
            <Card>
              <div className="h2" style={{ marginBottom: 4 }}>
                {t('Призы и шансы')}
              </div>
              <p className="sub" style={{ marginBottom: 10 }}>
                {t('Вероятности открыты и не меняются во время акции.')}
              </p>
              {odds.map((o, i) => (
                <div className="prow" key={`${o.label}-${i}`}>
                  <span className="ic">
                    <Icon name="gift" size={14} />
                  </span>
                  <div>
                    <div className="t">{o.label}</div>
                    {o.points ? <div className="d">{t('+{n} баллов', { n: o.points })}</div> : null}
                  </div>
                  <span className="od">{percent(o.weight, total ?? 100)}%</span>
                </div>
              ))}
            </Card>
          )}

          <Card>
            <KeyRow label={t('Организатор')}>{c.organizer || '—'}</KeyRow>
            <KeyRow label={t('Сроки')}>
              {fmtDay(c.start_at)} — {fmtDay(c.end_at)}
            </KeyRow>
            {c.draw_at && <KeyRow label={t('Розыгрыш')}>{fmtDateTime(c.draw_at)}</KeyRow>}
            <KeyRow label={t('Выдача приза')}>
              {t('{n} дней на оформление', { n: c.handout_days })}
            </KeyRow>
            <KeyRow label={t('Контакт')}>{c.contact || '—'}</KeyRow>
            {c.rules_hash && (
              <KeyRow label={t('Хеш правил')} mono>
                <Hash value={c.rules_hash} />
              </KeyRow>
            )}
            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <Button variant="out" block onClick={() => setRulesOpen((v) => !v)}>
                {rulesOpen ? t('Скрыть правила') : t('Полные правила акции')}
              </Button>
              {/* Хеш без способа его проверить — просто строка символов. */}
              <Link to={`/rules/${c.id}`} className="btn out block">
                {t('Проверить хеш')}
              </Link>
            </div>
            {rulesOpen && (
              <pre
                className="mono"
                style={{
                  marginTop: 12,
                  whiteSpace: 'pre-wrap',
                  fontSize: 11.5,
                  lineHeight: 1.7,
                  color: 'var(--dim)',
                }}
              >
                {c.rules_text}
              </pre>
            )}
          </Card>

          <Fairness fair={fair} campaignId={c.id} hasDraw={hasDraw} random={randomOutcome(c.recipe)} />
        </div>
      )}

      {tab === 'part' && <Participants campaignId={c.id} initial={participants ?? []} mine={myNumbers} total={c.tickets} />}

      {tab === 'win' && (
        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            {t('Победители')}
          </div>
          {(winners ?? [])
            .filter((w) => w.kind !== 'reserve')
            .map((w) => (
              <div className={`wrow ${myNumbers.has(w.ticket_number) ? 'mine' : ''}`} key={w.position}>
                <span className="rk">#{w.position}</span>
                {w.pick ? <span className="pickmark">{w.pick}</span> : null}
                <span className="num">{w.ticket_number}</span>
                <span className="rg">{[w.region, prizeState(w, t)].filter(Boolean).join(' · ')}</span>
              </div>
            ))}

          {(winners ?? []).some((w) => w.kind === 'reserve') && (
            <>
              <div className="lab" style={{ margin: '14px 0 8px' }}>
                {t('Запасные')}
              </div>
              {(winners ?? [])
                .filter((w) => w.kind === 'reserve')
                .map((w) => (
                  <div className={`wrow ${myNumbers.has(w.ticket_number) ? 'mine' : ''}`} key={w.position}>
                    <span className="rk">{t('зап.')}</span>
                    {w.pick ? <span className="pickmark">{w.pick}</span> : null}
                    <span className="num">{w.ticket_number}</span>
                    <span className="rg">
                      {w.status === 'promoted'
                          ? t('занял место #{pos}', { pos: w.took_pos ?? 0 })
                          : t('ждёт очереди')}
                    </span>
                  </div>
                ))}
              <p className="sub" style={{ marginTop: 10 }}>
                {t('Запасные разыграны тем же сидом и в том же проходе, что победители, и опубликованы сразу. Если приз не забрали в срок, место переходит следующему по списку — это не новый розыгрыш, а шаг по уже опубликованному порядку.')}
              </p>
            </>
          )}

          <p className="sub" style={{ marginTop: 10 }}>
            {t('Персональные данные не публикуются — только номер билета, страна и что стало с призом.')}
          </p>
        </Card>
      )}

      {tab === 'res' && (view.data.reviews?.length ?? 0) > 0 && (
        <Card style={{ marginBottom: 14 }}>
          <div className="h2" style={{ marginBottom: 8 }}>
            {t('Отзывы о призах этой акции')}
          </div>
          <ReviewList reviews={view.data.reviews!} />
        </Card>
      )}

      {tab === 'res' && (
        <Card>
          <div className="h2" style={{ marginBottom: 10 }}>
            {t('Последние результаты')}
          </div>
          {(results ?? []).length === 0 ? (
            <Empty title={t('Пока никто не играл')} />
          ) : (
            (results ?? []).map((o) => (
              <div className="wrow" key={o.id}>
                <span className="num">{o.prize_label}</span>
                <span className="rg">{fmtShortDay(o.created_at)}</span>
              </div>
            ))
          )}
        </Card>
      )}
    </Shell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="mono" style={{ fontSize: 17, fontWeight: 600, color: 'var(--head)' }}>
        {value}
      </div>
      <div className="faint" style={{ fontSize: 11 }}>
        {label}
      </div>
    </div>
  );
}

/**
 * Открытый список участников с подгрузкой.
 *
 * Листаем курсором по номеру билета, а не offset-ом: список растёт во время
 * акции, и смещение сдвинуло бы страницы.
 */
function Participants({
  campaignId,
  initial,
  mine,
  total,
}: {
  campaignId: string;
  initial: PublicTicket[];
  mine: Set<string>;
  total: number;
}) {
  const t = useT();
  const [rows, setRows] = useState<PublicTicket[]>(initial);
  const [cursor, setCursor] = useState<number | null>(initial.length ? null : 0);
  const [busy, setBusy] = useState(false);

  const loadMore = async () => {
    setBusy(true);
    try {
      const last = rows.at(-1);
      const after = cursor ?? (last ? Number(last.number.split('-').pop()) : 0);
      const page = await api.participants(campaignId, after, 100);
      setRows((prev) => [...prev, ...page.participants]);
      setCursor(page.next);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="h2">{t('Участники')}</span>
        <span className="spacer" />
        <span className="faint mono" style={{ fontSize: 12 }}>
          {t('{shown} из {total}', { shown: num(rows.length), total: num(total) })}
        </span>
      </div>
      {rows.map((p) => (
        <div className={`wrow ${mine.has(p.number) ? 'mine' : ''}`} key={p.number}>
          {p.pick ? <span className="pickmark">{p.pick}</span> : null}
          <span className="num">{p.number}</span>
          <span className="rg">
            {[p.region, p.gender, p.member_for ? t('в ALLSWIPES с {since}', { since: p.member_for ?? '' }) : '', fmtShortDay(p.created_at)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ))}
      <p className="sub" style={{ marginTop: 10 }}>
        {t('Рядом с номером — цифра участника, страна, пол (если указан), месяц регистрации и время участия. Ни имени, ни города, ни контактов здесь нет и не будет.')}
      </p>
      {rows.length < total && (
        <div style={{ marginTop: 12 }}>
          <Button variant="out" block disabled={busy} onClick={loadMore}>
            {busy ? t('Загружаем…') : t('Показать ещё')}
          </Button>
        </div>
      )}
      <p className="sub" style={{ marginTop: 10 }}>
        {t('Список фиксируется до объявления публичного сида — по нему и считаются победители.')}
      </p>
    </Card>
  );
}

/**
 * Получение оффера.
 *
 * Механика без целевого действия в привычном смысле: платформа ничего не
 * разыгрывает и ничего не выдаёт — она сводит человека с готовым
 * предложением. Поэтому кнопка одна, и за ней сразу ссылка.
 *
 * Обе границы показываются до нажатия: «осталось 12» — это состояние
 * счётчика, а не приём продавца. Человек должен видеть, сколько предложений
 * было и сколько разобрали, прежде чем решит.
 *
 * Сама подпись уезжает наверх, на экран: здесь она попадала внутрь ряда
 * кнопок и вставала там отдельной колонкой из слов в столбик. Ошибки тоже
 * идут туда — человеку всё равно, что сломалось, ему важно одно место, где
 * написано, что происходит.
 */
function OfferAction({
  campaignId,
  linkKey,
  setNote,
}: {
  campaignId: string;
  linkKey: string;
  setNote: (note: string) => void;
}) {
  const t = useT();
  const [view, setView] = useState<OfferView | null>(null);
  const [busy, setBusy] = useState(false);

  // Что написать под кнопками. Без лимита «осталось» не существует:
  // показывать «осталось ∞» значит придумывать границу, которой нет.
  const note = (v: OfferView): string =>
    v.limit
      ? t('Выдано {taken} из {limit}. Осталось {left}.', {
          taken: num(v.taken),
          limit: num(v.limit),
          left: num(v.left),
        })
      : t('Выдано {taken}. Количество не ограничено.', { taken: num(v.taken) });

  useEffect(() => {
    let alive = true;
    void api
      .offer(campaignId)
      .then((v) => {
        if (!alive) return;
        setView(v);
        setNote(note(v));
      })
      .catch(() => {
        // Экран акции не должен ломаться из-за блока оффера: без него
        // человек всё равно видит условия, сроки и правила.
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const take = async () => {
    setBusy(true);
    try {
      const got = await api.takeOffer(campaignId, linkKey || undefined);
      setView(got);
      setNote(note(got));
      if (got.url) window.open(got.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : t('Не получилось'));
    } finally {
      setBusy(false);
    }
  };

  if (!view) return null;

  if (view.mine && view.url) {
    return (
      <a href={view.url} target="_blank" rel="noopener noreferrer" className="btn pri block">
        {t('Открыть предложение')}
      </a>
    );
  }
  return (
    <Button variant="pri" block disabled={busy || view.sold_out} onClick={() => void take()}>
      {view.sold_out ? t('Предложения закончились') : busy ? t('Получаем…') : t('Получить')}
    </Button>
  );
}

/**
 * randomOutcome — есть ли в механике случайность, которую нужно доказывать.
 *
 * Зеркало domain.Recipe.RandomOutcome на сервере: случайность есть у
 * розыгрыша и у форм призов, где исход решает вес или позиция. Остальным
 * механикам доказывать нечего — приз напечатан на упаковке, скидка зависит от
 * номера участия, оффер выдаётся по ссылке, — и блок про сид там врёт.
 */
function randomOutcome(recipe: string): boolean {
  switch (recipe) {
    case 'raffle':
    case 'wheel':
    case 'scratch':
    case 'plinko':
    case 'mystery':
    case 'collection':
      return true;
    default:
      return false;
  }
}

/**
 * howTo — три шага участия, свои у каждой механики.
 *
 * Раньше шаги были прибиты гвоздями: «найди код → отсканируй → получи билет».
 * Для половины механик это неправда. Человек, открывший оффер, читал, что
 * нужно искать код на упаковке и что исход считает сервер по сиду, — а у
 * оффера нет ни кода, ни случайности. Неверная инструкция хуже отсутствующей:
 * по ней человек идёт в магазин.
 */
function howTo(
  c: Campaign,
  hasDraw: boolean,
  t: (s: string, vars?: Record<string, string | number>) => string,
): Array<{ title: string; body: string }> {
  const place = c.code_place || t('на упаковке');
  switch (c.recipe) {
    case 'offer': {
      const limit = c.config.offer?.limit ?? 0;
      return [
        { title: t('Открой акцию'), body: t('Покупка и код не нужны.') },
        { title: t('Нажми «Получить»'), body: t('Предложение откроется сразу, по ссылке.') },
        {
          title: t('Пользуйся'),
          body: limit
            ? t('Всего предложений: {n}, по одному на человека.', { n: limit })
            : t('Количество не ограничено.'),
        },
      ];
    }
    case 'pack':
      return [
        {
          title: t('Купи товар'),
          body: t('Приз напечатан {place} — производителем, до покупки.', { place }),
        },
        {
          title: t('Посмотри, что выпало'),
          body: t('Платформа исход не выбирает: он уже на упаковке.'),
        },
        {
          title: t('Приди с упаковкой'),
          body: t('В точку из списка ниже. Сколько призов выдано — видно там же.'),
        },
      ];
    case 'data4value':
      return [
        { title: t('Открой акцию'), body: t('Покупка и код не нужны.') },
        { title: t('Посмотри, что просят'), body: t('Список полей и твои значения — до передачи.') },
        {
          title: t('Реши сам'),
          body: t('Откажешься — ничего не произойдёт. Награда даётся один раз.'),
        },
      ];
    case 'quiz':
      return [
        { title: t('Открой квиз'), body: t('Покупка и код не нужны.') },
        { title: t('Ответь на вопросы'), body: t('Правильные ответы открываются сразу.') },
        { title: t('Получи баллы'), body: t('Начисляются за попытку, одну на человека.') },
      ];
    case 'streak':
      return [
        { title: t('Открой акцию'), body: t('Покупка и код не нужны.') },
        { title: t('Отмечайся каждый день'), body: t('Пропуск обнуляет серию.') },
        { title: t('Получи приз'), body: t('Когда серия дойдёт до конца.') },
      ];
    case 'referral':
      return [
        { title: t('Возьми свою ссылку'), body: t('Она привязана к тебе и видна в акции.') },
        { title: t('Позови друга'), body: t('Баллы начислятся, когда он поучаствует.') },
        { title: t('Получи баллы'), body: t('И ты, и друг — суммы объявлены заранее.') },
      ];
    case 'quest':
      return [
        { title: t('Открой цепочку'), body: t('Шаги видны целиком до начала.') },
        {
          title: t('Проходи шаги по порядку'),
          body: t('Пропустить нельзя, награда за шаг — сразу.'),
        },
        { title: t('Забери приз'), body: t('За всю цепочку, после последнего шага.') },
      ];
    case 'team':
      return [
        { title: t('Найди код'), body: t('Уникальный код {place}.', { place }) },
        {
          title: t('Внеси вклад'),
          body: c.config.team?.goal
            ? t('Нужно {n} участий — счётчик общий и открытый.', { n: c.config.team.goal })
            : t('Счётчик общий и открытый.'),
        },
        { title: t('Получите все'), body: t('Приз достаётся каждому участнику, включая первых.') },
      ];
    case 'dynamic':
      return [
        { title: t('Найди код'), body: t('Уникальный код {place}.', { place }) },
        { title: t('Участвуй раньше'), body: t('Ступень скидки зависит от номера участия.') },
        { title: t('Получи цену'), body: t('Когда ступени кончатся, код не потратится.') },
      ];
    default:
      return [
        { title: t('Найди код'), body: t('Уникальный код {place}.', { place }) },
        { title: t('Отсканируй или введи'), body: t('Один код принимается один раз.') },
        hasDraw
          ? {
              title: t('Получи билет'),
              body: t('Номер попадёт в открытый список участников и в розыгрыш.'),
            }
          : {
              title: t('Получи приз сразу'),
              body: t('Исход считает сервер по заранее зафиксированному сиду.'),
            },
      ];
  }
}

/**
 * needsCode — нужен ли этой механике код с упаковки.
 *
 * Квиз, серия и приглашения начинаются в приложении: предлагать там «отсканируй
 * код» значит отправить человека искать то, чего нет. У приглашений код всё же
 * есть — им подтверждается участие друга, — но входная точка всё равно ссылка.
 *
 * «Приз на упаковке» — единственная механика, где целевого действия нет
 * вообще: приз напечатал завод, и сканировать нечего. Человеку нужен не
 * экран, а адрес.
 *
 * У оффера кнопка своя, и она же единственная. У анкеты предмет сделки —
 * данные, и кода тоже нет: вторая кнопка «Отсканировать код» рядом с «Посмотри
 * условия обмена» отправляла человека искать то, чего акция не печатала.
 */
function needsCode(recipe: string): boolean {
  switch (recipe) {
    case 'quiz':
    case 'streak':
    case 'referral':
    case 'pack':
    case 'offer':
    case 'data4value':
      return false;
    default:
      return true;
  }
}

/** Подпись правила повторного участия — та же, что в правилах акции. */
function repeatLabel(period: string): string {
  switch (period) {
    case 'never':
      return 'Один раз на человека';
    case 'hour':
      return 'Не чаще раза в час';
    case 'day':
      return 'Не чаще раза в день';
    case 'week':
      return 'Не чаще раза в неделю';
    case 'month':
      return 'Не чаще раза в месяц';
    default:
      return 'Без ограничений по числу участий';
  }
}

/** Названия запрошенных полей — те же, что человек увидит в согласии. */
function fieldLabels(keys: string[], t: (text: string) => string): string {
  const names: Record<string, string> = {
    name: 'имя',
    city: 'город',
    birthday: 'дату рождения',
    gender: 'пол',
    email: 'почту',
    kids: 'детей в семье',
    car: 'автомобиль',
    favorite: 'любимый продукт',
  };
  return keys.map((k) => t(names[k] ?? k)).join(', ');
}

/** Что стало с призом победителя — одной строкой для списка. */
function prizeState(
  w: { status?: string; claim_status?: string; replaced_by?: number },
  t: (text: string, vars?: Record<string, string | number>) => string,
): string {
  if (w.status === 'forfeited') {
    return w.replaced_by
      ? t('не забрал — место переиграно (#{pos})', { pos: w.replaced_by })
      : t('не забрал приз');
  }
  switch (w.claim_status) {
    case 'received':
    case 'reviewed':
      return t('приз получен');
    case 'ready':
      return t('приз готов к выдаче');
    case 'processing':
      return t('оформляется');
    case 'expired':
      return t('срок оформления вышел');
    case 'unclaimed':
      return t('ещё не оформил');
    default:
      return '';
  }
}
